"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { fetchAggregateScore } from "@/lib/leaderboard";

interface IntroScreenProps {
  runStatus: "none" | "active" | "completed";
  isGuest: boolean;
  onContinue: () => void;
  onTryDemo: () => void;
}

export default function IntroScreen({
  runStatus,
  isGuest,
  onContinue,
  onTryDemo,
}: IntroScreenProps) {
  const [heroTotal, setHeroTotal] = useState<number | null>(null);
  const [villainTotal, setVillainTotal] = useState<number | null>(null);

  useEffect(() => {
    fetchAggregateScore("hero").then(setHeroTotal).catch(() => setHeroTotal(0));
    fetchAggregateScore("villain")
      .then(setVillainTotal)
      .catch(() => setVillainTotal(0));
  }, []);

  return (
    <main className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
      <div className="animate-fade-in flex max-w-2xl flex-col items-center">
        <h1 className="text-[clamp(2.5rem,9vw,5.5rem)] font-black uppercase leading-[1.05] tracking-tight [text-shadow:0_0_60px_rgba(124,58,237,0.5)]">
          Hero or a<span className="text-accent"> Villain?</span>
        </h1>

        <p className="mt-6 max-w-md text-[clamp(0.95rem,2vw,1.125rem)] leading-relaxed text-white/60">
          Spin the wheels of fate. Discover your powers, your weakness, your
          rank. Are you the chosen one — or an NPC? No choices. Just destiny.
        </p>

        <div className="mt-12 flex items-center gap-[clamp(1rem,4vw,3rem)]">
          <div className="flex flex-col items-center gap-1">
            <div className="text-[clamp(0.65rem,1.3vw,0.8rem)] font-bold uppercase tracking-[0.3em] text-accent">
              Hero
            </div>
            <div className="font-mono text-[clamp(1.25rem,4vw,2.25rem)] font-extrabold">
              {heroTotal === null ? "—" : heroTotal.toLocaleString()}
            </div>
          </div>
          <div className="text-[clamp(0.8rem,2vw,1.25rem)] font-black uppercase tracking-widest text-white/30">
            vs
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="text-[clamp(0.65rem,1.3vw,0.8rem)] font-bold uppercase tracking-[0.3em] text-red-400">
              Villain
            </div>
            <div className="font-mono text-[clamp(1.25rem,4vw,2.25rem)] font-extrabold">
              {villainTotal === null ? "—" : villainTotal.toLocaleString()}
            </div>
          </div>
        </div>

        <button
          onClick={onContinue}
          className="mt-14 min-w-[12rem] rounded-full bg-accent px-[clamp(2.5rem,6vw,4rem)] py-[clamp(0.75rem,2vh,1rem)] text-[clamp(1rem,2.2vw,1.375rem)] font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_40px_rgba(124,58,237,0.6)] transition hover:brightness-110"
        >
          {runStatus === "completed"
            ? "View Stats"
            : runStatus === "active"
              ? "Continue Run"
              : "Begin"}
        </button>

        {/* Secondary, always-available demo entry. */}
        <button
          onClick={onTryDemo}
          className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-white/50 underline-offset-4 transition hover:text-amber-300 hover:underline"
        >
          🎲 Try a Demo Run
        </button>

        {isGuest && (
          <div className="mt-6 text-xs uppercase tracking-widest text-white/30">
            Sign in to save your results permanently —{" "}
            <Link href="/login" className="font-bold text-accent hover:underline">
              Sign In
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
